import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { MotionConfig, motion, useReducedMotion } from 'framer-motion';
import { LuArrowLeft, LuCheck, LuCode, LuFlaskConical, LuHouse, LuLoaderCircle, LuPenTool } from 'react-icons/lu';
import Button from '../components/ui/Button';
import { useTranslation } from '../i18n/LanguageContext';

const LOOP = 6; // seconds of one build cycle
const STEPS = [
  { key: 'design', icon: LuPenTool },
  { key: 'build', icon: LuCode },
  { key: 'test', icon: LuFlaskConical },
];

const Gear = ({ cx, cy, r, teeth, reverse, className }) => {
  const points = [];
  for (let i = 0; i < teeth * 2; i += 1) {
    const angle = (Math.PI * i) / teeth;
    const radius = i % 2 === 0 ? r : r * 0.78;
    points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`);
  }
  return (
    <motion.g
      style={{ originX: 0.5, originY: 0.5 }}
      animate={{ rotate: reverse ? -360 : 360 }}
      transition={{ duration: reverse ? 7 : 9, ease: 'linear', repeat: Infinity }}
    >
      <polygon points={points.join(' ')} className={className} />
      <circle cx={cx} cy={cy} r={r * 0.32} className="tw-fill-white dark:tw-fill-[#1b1f27]" />
    </motion.g>
  );
};

Gear.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  r: PropTypes.number.isRequired,
  teeth: PropTypes.number.isRequired,
  reverse: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * The site being built: a crane lowers a block onto a growing building while
 * gears turn, on a blueprint grid. One cycle repeats every LOOP seconds; with
 * "reduce motion" everything stands still in its finished pose.
 */
const BuildScene = () => {
  const blocks = [
    { x: 92, y: 138, w: 46, delay: 0.2, tone: 'tw-fill-teal-400' },
    { x: 142, y: 138, w: 46, delay: 0.5, tone: 'tw-fill-cyan-500' },
    { x: 192, y: 138, w: 46, delay: 0.8, tone: 'tw-fill-teal-500' },
    { x: 117, y: 108, w: 46, delay: 1.3, tone: 'tw-fill-sky-400' },
    { x: 167, y: 108, w: 46, delay: 1.6, tone: 'tw-fill-teal-400' },
  ];
  return (
    <svg viewBox="0 0 320 200" className="tw-h-auto tw-w-full tw-max-w-[22rem]" aria-hidden="true">
      <defs>
        <pattern id="uc-grid" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M16 0H0V16" fill="none" strokeWidth="0.6" className="tw-stroke-teal-500/20 dark:tw-stroke-teal-300/15" />
        </pattern>
        <linearGradient id="uc-beam" x1="0" x2="1">
          <stop offset="0" stopColor="#14b8a6" />
          <stop offset="1" stopColor="#0891b2" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height="200" rx="22" fill="url(#uc-grid)" />

      {/* Ground */}
      <rect x="40" y="168" width="240" height="4" rx="2" className="tw-fill-slate-300 dark:tw-fill-white/15" />

      {/* Building blocks settle in one after another */}
      {blocks.map((block) => (
        <motion.rect
          key={`${block.x}-${block.y}`}
          x={block.x}
          width={block.w}
          height="28"
          rx="5"
          className={block.tone}
          initial={{ y: block.y - 60, opacity: 0 }}
          animate={{ y: [block.y - 60, block.y, block.y, block.y], opacity: [0, 1, 1, 0] }}
          transition={{ duration: LOOP, times: [block.delay / LOOP, (block.delay + 0.6) / LOOP, 0.9, 1], repeat: Infinity, ease: 'easeOut' }}
        />
      ))}

      {/* Windows blink on once the floor is up */}
      {[104, 154, 204].map((x, index) => (
        <motion.rect
          key={x}
          x={x}
          y="146"
          width="20"
          height="10"
          rx="2"
          className="tw-fill-white/80"
          animate={{ opacity: [0, 0, 1, 1, 0] }}
          transition={{ duration: LOOP, times: [0, 0.35 + index * 0.05, 0.42 + index * 0.05, 0.9, 1], repeat: Infinity }}
        />
      ))}

      {/* Crane */}
      <rect x="258" y="40" width="7" height="128" rx="2" className="tw-fill-slate-400 dark:tw-fill-slate-500" />
      <rect x="140" y="36" width="140" height="7" rx="3" fill="url(#uc-beam)" />
      <rect x="262" y="28" width="22" height="10" rx="3" className="tw-fill-amber-400" />
      <motion.g
        animate={{ x: [0, -60, -60, 0, 0], rotate: [0, 0, 3, -3, 0] }}
        transition={{ duration: LOOP, times: [0, 0.3, 0.55, 0.8, 1], repeat: Infinity, ease: 'easeInOut' }}
        style={{ originX: 0.5, originY: 0 }}
      >
        <motion.line
          x1="230"
          y1="43"
          x2="230"
          className="tw-stroke-slate-500 dark:tw-stroke-slate-400"
          strokeWidth="1.5"
          animate={{ y2: [70, 92, 92, 70, 70] }}
          transition={{ duration: LOOP, times: [0, 0.3, 0.55, 0.8, 1], repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.rect
          x="214"
          width="32"
          height="18"
          rx="4"
          className="tw-fill-amber-400"
          animate={{ y: [70, 92, 92, 70, 70], opacity: [1, 1, 0, 0, 1] }}
          transition={{ duration: LOOP, times: [0, 0.3, 0.55, 0.8, 1], repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.g>

      {/* Gears */}
      <Gear cx={52} cy={58} r={22} teeth={10} className="tw-fill-teal-500/80 dark:tw-fill-teal-400/70" />
      <Gear cx={86} cy={36} r={14} teeth={8} reverse className="tw-fill-cyan-500/70 dark:tw-fill-cyan-400/60" />

      {/* Sparkles where a block lands */}
      {[[120, 100], [210, 128], [168, 96]].map(([x, y], index) => (
        <motion.circle
          key={`${x}-${y}`}
          cx={x}
          cy={y}
          r="2.5"
          className="tw-fill-amber-300"
          animate={{ scale: [0, 1.4, 0], opacity: [0, 1, 0] }}
          transition={{ duration: 1.2, delay: 0.6 + index * 0.7, repeat: Infinity, repeatDelay: LOOP - 1.2 }}
        />
      ))}
    </svg>
  );
};

/** Design → build → test, the current step moving along every few seconds. */
const useCurrentStep = () => {
  const reduced = useReducedMotion();
  const [step, setStep] = useState(1);
  useEffect(() => {
    if (reduced) return undefined;
    const timer = setInterval(() => setStep((current) => (current + 1) % STEPS.length), 2400);
    return () => clearInterval(timer);
  }, [reduced]);
  return step;
};

/**
 * Shown for pages that are not ready yet (the catch-all route): a building
 * site animation, what is going on, the address asked for, and the way back.
 */
const NotFoundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const label = (key) => t(`underConstruction.${key}`);
  const step = useCurrentStep();

  return (
    <MotionConfig reducedMotion="user">
      <div lang={language} className="tw-flex tw-min-h-[calc(100vh-7rem)] tw-items-center tw-justify-center tw-px-4 tw-py-8 tw-font-sans tw-antialiased">
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="tw-relative tw-isolate tw-w-full tw-max-w-xl tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-white/80 tw-bg-white/75 tw-px-6 tw-pb-8 tw-pt-6 tw-text-center tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_30px_80px_-40px_rgba(15,23,42,0.45)] sm:tw-px-10 dark:tw-border-white/10 dark:tw-bg-[#22262f]/85"
        >
          <span aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-24 -tw-top-24 -tw-z-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-teal-300/40 tw-blur-3xl dark:tw-bg-teal-500/15" />
          <span aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-bottom-28 -tw-right-16 -tw-z-10 tw-h-72 tw-w-72 tw-rounded-full tw-bg-cyan-300/30 tw-blur-3xl dark:tw-bg-cyan-500/10" />

          <div className="tw-flex tw-justify-center">
            <BuildScene />
          </div>

          <p className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-amber-400/15 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-amber-700 dark:tw-text-amber-300">
            <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin motion-reduce:tw-animate-none" />
            {label('badge')}
          </p>
          <h1 className="tw-mt-3 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-3xl dark:tw-text-white">{label('title')}</h1>
          <p className="tw-mx-auto tw-mt-2 tw-max-w-md tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-400">{label('text')}</p>

          {/* Indeterminate progress with a moving sheen */}
          <div className="tw-mx-auto tw-mt-6 tw-h-2 tw-max-w-sm tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" role="progressbar" aria-label={label('progress')}>
            <motion.span
              className="tw-block tw-h-full tw-w-1/3 tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-via-cyan-400 tw-to-teal-500"
              animate={{ x: ['-110%', '330%'] }}
              transition={{ duration: 1.8, ease: 'easeInOut', repeat: Infinity }}
            />
          </div>

          <ol className="tw-mx-auto tw-mt-4 tw-flex tw-max-w-sm tw-items-center tw-justify-between tw-gap-2">
            {STEPS.map((item, index) => {
              const Icon = index < step ? LuCheck : item.icon;
              const current = index === step;
              return (
                <li key={item.key} className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-1.5">
                  <motion.span
                    animate={current ? { scale: [1, 1.12, 1] } : { scale: 1 }}
                    transition={{ duration: 1.2, repeat: current ? Infinity : 0 }}
                    className={`tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-full tw-transition-colors tw-duration-500 ${
                      current
                        ? 'tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-lg tw-shadow-teal-500/30'
                        : index < step
                          ? 'tw-bg-teal-500/15 tw-text-teal-700 dark:tw-text-teal-300'
                          : 'tw-bg-slate-900/[0.05] tw-text-slate-400 dark:tw-bg-white/[0.07]'
                    }`}
                  >
                    <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </motion.span>
                  <span className={`tw-text-xs tw-font-semibold ${current ? 'tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>
                    {label(`steps.${item.key}`)}
                  </span>
                </li>
              );
            })}
          </ol>

          {location.pathname && location.pathname !== '/' && (
            <p className="tw-mt-5 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">
              {label('address')}{' '}
              <code className="tw-rounded-md tw-bg-slate-900/[0.05] tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[11px] tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">{location.pathname}</code>
            </p>
          )}

          <div className="tw-mt-6 tw-flex tw-flex-col-reverse tw-items-stretch tw-justify-center tw-gap-2 sm:tw-flex-row sm:tw-items-center">
            <Button onClick={() => navigate(-1)}>
              <LuArrowLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('back')}
            </Button>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              <LuHouse aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('home')}
            </Button>
          </div>
        </motion.section>
      </div>
    </MotionConfig>
  );
};

export default NotFoundPage;
